import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '../../theme';
import { warRoom } from '../../language';
import { useMobile } from '../../hooks/useMediaQuery';

/* ─── Types ─────────────────────────────────────────────── */
interface AIInsight {
  id: string;
  type: 'critical' | 'warning' | 'success' | 'tip';
  title: string;
  message: string;
  action?: string;
  detail?: string;
}

interface AICoachProps {
  analysisResult?: any;
  userFinancials?: any;
}

/* ─── Component ─────────────────────────────────────────── */
export const AICoach = ({ analysisResult, userFinancials }: AICoachProps) => {
  const mobile = useMobile();
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const items: AIInsight[] = [];

    const d = warRoom.defaultInsights;

    if (analysisResult?.warrior_analysis) {
      items.push({
        id: '1', type: 'critical', title: d.assessment.title,
        message: analysisResult.warrior_analysis.warrior_assessment || d.assessment.message,
        action: d.assessment.action,
        detail: d.assessment.detail,
      });
    }

    if (analysisResult?.summary?.total_expenses) {
      const exp = Math.abs(analysisResult.summary.total_expenses);
      if (exp > 3000) {
        items.push({
          id: '2', type: 'warning', title: d.highSpending.title,
          message: d.highSpending.message(exp.toFixed(0)),
          action: d.highSpending.action,
          detail: d.highSpending.detail,
        });
      }
    }

    if (analysisResult?.summary?.net_position && analysisResult.summary.net_position < 0) {
      items.push({
        id: '3', type: 'critical', title: d.negativeCashFlow.title,
        message: d.negativeCashFlow.message(Math.abs(analysisResult.summary.net_position).toFixed(0)),
        action: d.negativeCashFlow.action,
        detail: d.negativeCashFlow.detail,
      });
    }

    items.push({
      id: '4', type: 'tip', title: d.psychological.title,
      message: d.psychological.message,
      detail: d.psychological.detail,
    });

    items.push({
      id: '5', type: 'success', title: d.momentum.title,
      message: d.momentum.message,
      action: d.momentum.action,
      detail: d.momentum.detail,
    });

    setInsights(items);
  }, [analysisResult, userFinancials]);

  return (
    <div>
      {/* Section header — flat, bold */}
      <div style={{
        marginBottom: mobile ? theme.spacing[4] : theme.spacing[6],
        borderBottom: `${theme.borders.heavy} ${theme.brand.crimson}`,
        paddingBottom: theme.spacing[3],
      }}>
        <h2 style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: mobile ? theme.typography.fontSize.xl : theme.typography.fontSize['2xl'],
          fontWeight: theme.typography.fontWeight.black,
          color: theme.semantic.text.primary,
          letterSpacing: theme.typography.letterSpacing.widest,
          textTransform: 'uppercase' as const,
          margin: 0,
        }}>
          {warRoom.heading}
        </h2>
        <p style={{
          fontFamily: theme.typography.fontFamily.sans,
          fontSize: theme.typography.fontSize.sm,
          color: theme.semantic.text.muted,
          margin: `${theme.spacing[1]} 0 0 0`,
        }}>
          {warRoom.subtitle(insights.length)}
        </p>
      </div>

      {/* Insight cards — flat, left-bordered */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3] }}>
        <AnimatePresence>
          {insights.map((insight, i) => {
            const cfg = theme.insightConfig[insight.type];
            const expanded = expandedId === insight.id;

            return (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setExpandedId(expanded ? null : insight.id)}
                style={{
                  background: cfg.bg,
                  borderLeft: `${theme.borders.heavy} ${cfg.color}`,
                  padding: mobile ? theme.spacing[4] : theme.spacing[5],
                  cursor: 'pointer',
                }}
              >
                {/* Type label */}
                <div style={{
                  fontFamily: theme.typography.fontFamily.display,
                  fontSize: '0.65rem',
                  fontWeight: theme.typography.fontWeight.bold,
                  color: cfg.color,
                  letterSpacing: theme.typography.letterSpacing.widest,
                  marginBottom: theme.spacing[2],
                }}>
                  {cfg.label}
                </div>

                {/* Title */}
                <h3 style={{
                  fontFamily: theme.typography.fontFamily.display,
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.semantic.text.primary,
                  letterSpacing: theme.typography.letterSpacing.wide,
                  textTransform: 'uppercase' as const,
                  margin: `0 0 ${theme.spacing[2]} 0`,
                }}>
                  {insight.title}
                </h3>

                {/* Message */}
                <p style={{
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.semantic.text.secondary,
                  lineHeight: theme.typography.lineHeight.relaxed,
                  margin: 0,
                }}>
                  {insight.message}
                </p>

                {/* Expanded detail */}
                <AnimatePresence>
                  {expanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div style={{
                        marginTop: theme.spacing[4],
                        paddingTop: theme.spacing[4],
                        borderTop: `1px solid ${cfg.color}`,
                      }}>
                        {insight.detail && (
                          <p style={{
                            fontSize: theme.typography.fontSize.sm,
                            color: theme.semantic.text.secondary,
                            lineHeight: theme.typography.lineHeight.relaxed,
                            margin: 0,
                          }}>
                            {insight.detail}
                          </p>
                        )}

                        {insight.action && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(`Action: ${insight.action}`);
                            }}
                            style={{
                              marginTop: theme.spacing[3],
                              padding: `${theme.spacing[2]} ${mobile ? theme.spacing[4] : theme.spacing[5]}`,
                              background: cfg.color,
                              border: 'none',
                              color: theme.brand.cream,
                              fontFamily: theme.typography.fontFamily.display,
                              fontSize: theme.typography.fontSize.xs,
                              fontWeight: theme.typography.fontWeight.bold,
                              letterSpacing: theme.typography.letterSpacing.widest,
                              textTransform: 'uppercase' as const,
                              cursor: 'pointer',
                            }}
                          >
                            {insight.action}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: theme.spacing[8],
        paddingTop: theme.spacing[4],
        borderTop: `1px solid ${theme.semantic.border.default}`,
      }}>
        <p style={{
          fontFamily: theme.typography.fontFamily.sans,
          fontSize: theme.typography.fontSize.sm,
          color: theme.semantic.text.disabled,
          fontStyle: 'italic',
          margin: 0,
        }}>
          {warRoom.emptyState}
        </p>
      </div>
    </div>
  );
};

export default AICoach;
